<template>
  <div>
    <div class="box">
      <loading v-if="isLoading" />

      <div v-if="!isLoading">
        <div class="columns">
          <div class="column">
            <h2 class="title has-text-primary">
              <fa-icon class="margin-right-2" :icon="faUsers" />User Groups
            </h2>
          </div>
          <div class="column is-narrow">
            <div class="buttons">
              <button class="button is-primary is-outlined margin-right-2" @click="createUserGroup">
                <fa-icon :icon="faPlus" />Add
              </button>
              <custom-button modifier="primary" :icon="faSyncAlt" iconPosition="left" @click="refresh">Refresh</custom-button>
            </div>
          </div>
        </div>

        <div class="panel">
          <div class="panel-heading">
            <div class="columns">
              <div class="column">
                <div class="margin-top-1">Filter Options</div>
              </div>
              <div class="column margin-top-1 is-narrow">{{filteredUserGroups.length}} result(s)</div>

              <div class="column is-narrow">
                <button class="button is-primary is-outlined" @click="resetFilters">
                  <fa-icon :icon="faUndo" />Reset Filters
                </button>
              </div>
            </div>
          </div>
          <div class="panel-block">
            <p class="control has-icons-left">
              <input class="input" v-model="searchTerm" placeholder="Search..." />
              <span class="icon is-left">
                <fa-icon :icon="faSearch" />
              </span>
            </p>
          </div>
        </div>

        <table class="table is-striped is-hoverable is-fullwidth" v-if="filteredUserGroups.length > 0">
          <tbody>
            <tr v-for="(userGroup, index) in filteredUserGroups" :key="index">
              <td>{{userGroup.title}}</td>
              <td class="is-narrow">
                <div class="buttons is-pulled-right is-inline-block" v-if="userGroup.isDefaultGroup === false">
                  <custom-button class="is-inline-block margin-right-2" modifier="primary" :icon="faUsersCog" iconPosition="left" @click="editUserGroup(userGroup)">Edit</custom-button>
                  <custom-button class="is-inline-block" modifier="danger" :icon="faUsersSlash" iconPosition="left" @click="deleteUserGroup(userGroup)">Delete</custom-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="has-text-centered" v-if="userGroups.length === 0">No user groups found.</div>
        <div class="has-text-centered" v-if="userGroups.length > 0 && filteredUserGroups.length === 0">No matching user groups found.</div>
      </div>
    </div>

    <modal v-if="showEditUserGroupModal" title="Edit User Group" @close="cancelEditingUserGroup" @submit="saveEditedUserGroup">
      <div class="field is-horizontal">
        <div class="field-label is-normal">
          <label class="label">Title</label>
        </div>
        <div class="field-body">
          <div class="field">
            <div class="control">
              <input class="input" :class="{'is-danger' : $v.userGroupBeingEdited.$dirty && $v.userGroupBeingEdited.title.$anyError}" type="text" v-model="$v.userGroupBeingEdited.title.$model" />
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.userGroupBeingEdited.$dirty && !$v.userGroupBeingEdited.title.required">{{$v.userGroupBeingEdited.title.$params.required.msg}}</div>
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.userGroupBeingEdited.$dirty && !$v.userGroupBeingEdited.title.maxLength">{{$v.userGroupBeingEdited.title.$params.maxLength.msg}}</div>
            </div>
          </div>
        </div>
      </div>
    </modal>

    <modal v-if="showCreateModal" title="Create User Group" @close="cancelCreatingUserGroup" @submit="saveNewUserGroup">
      <div class="field is-horizontal">
        <div class="field-label is-normal">
          <label class="label">Title</label>
        </div>
        <div class="field-body">
          <div class="field">
            <div class="control">
              <input class="input" :class="{'is-danger' : $v.newUserGroup.$dirty && $v.newUserGroup.title.$anyError}" type="text" v-model="$v.newUserGroup.title.$model" />
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.newUserGroup.$dirty && !$v.newUserGroup.title.required">{{$v.newUserGroup.title.$params.required.msg}}</div>
              <div class="help has-text-weight-semibold has-text-danger" v-if="$v.newUserGroup.$dirty && !$v.newUserGroup.title.maxLength">{{$v.newUserGroup.title.$params.maxLength.msg}}</div>
            </div>
          </div>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import loading from "@/components/loading"
import customButton from "@/components/custom-button"
import modal from "@/components/modal"
import { faUsers, faSyncAlt, faUsersCog, faUsersSlash, faSearch, faUndo, faPlus } from "@fortawesome/free-solid-svg-icons"
import { shallowCopy } from "@/utilities"
import { withParams } from 'vuelidate';
import { required, maxLength } from "vuelidate/lib/validators";

export default {
  components: {
    loading,
    customButton,
    modal
  },
  computed: {
    filteredUserGroups() {
      if (!this.searchTerm || this.searchTerm === "")
        return this.userGroups

      return this.userGroups.filter(x => x.title.toLowerCase().includes(this.searchTerm.toLowerCase()))
    }
  },
  data() {
    return {
      isLoading: true,
      userGroups: null,
      searchTerm: "",
      userGroupBeingEdited: {
        id: null,
        title: null,
        isDefaultGroup: null,
        hasUsersAssigned: null
      },
      showEditUserGroupModal: false,
      newUserGroup: {
        title: null
      },
      showCreateModal: false,
      faUsers,
      faSyncAlt,
      faUsersCog,
      faUsersSlash,
      faSearch,
      faUndo,
      faPlus
    }
  },
  methods: {
    refresh() {
      this.isLoading = true

      this.$http.get("UserGroups").then(response => {
        this.userGroups = response.data
        this.isLoading = false
      }).catch(() => {
        this.isLoading = false
        this.$alert("An error occurred loading the user groups.", "", "error");
      })
    },
    editUserGroup(userGroup) {
      this.$v.userGroupBeingEdited.$reset();

      this.userGroupBeingEdited = shallowCopy(userGroup);
      this.showEditUserGroupModal = true;
    },
    saveEditedUserGroup() {
      this.$v.userGroupBeingEdited.$touch();

      if (this.$v.userGroupBeingEdited.$invalid)
        return;

      this.isLoading = true

      var data = {
        id: this.userGroupBeingEdited.id,
        title: this.userGroupBeingEdited.title
      };

      this.$http.post("UserGroups", data).then(() => {
        this.showEditUserGroupModal = false;
        this.refresh();
        this.$alert("User group updated successfully.", "", "success");
      }).catch((error) => {
        if (error.response.data.message) {
          this.$alert(error.response.data.message, "", "error");
        } else this.$alert("An error occurred updating the user group.", "", "error");

        this.isLoading = false
      });
    },
    cancelEditingUserGroup() {
      this.showEditUserGroupModal = false;
    },
    deleteUserGroup(userGroup) {
      if (userGroup.isDefaultGroup === true) {
        this.$alert("The default user group cannot be deleted.", "User group required", "error");
      }
      else if (userGroup.hasUsersAssigned) {
        this.$alert("The user group '" + userGroup.title + "' could not be deleted as one or more users are currently assigned to it. Please update those users before attempting to delete the user group.", "User group in use", "error");
      }
      else {
        this.$confirm("Are you sure you wish to delete the user group '" + userGroup.title + "'?", "Delete user group?", "question").then(() => {
          this.$http.delete("UserGroups/" + userGroup.id).then(() => {
            this.refresh();
            this.$alert("User group deleted successfully.", "", "success");
          }).catch((error) => {
            if (error.response.data.message) {
              this.$alert(error.response.data.message, "", "error");
            } else this.$alert("An error occurred deleting the user group.", "", "error");
          });
        });
      }
    },
    resetFilters() {
      this.searchTerm = "";
    },
    createUserGroup() {
      this.newUserGroup = {
        title: null
      };

      this.$v.newUserGroup.$reset();

      this.showCreateModal = true;
    },
    saveNewUserGroup() {
      this.$v.newUserGroup.$touch();

      if (this.$v.newUserGroup.$invalid)
        return;

      this.isLoading = true

      this.$http.put("UserGroups", this.newUserGroup).then(() => {
        this.showCreateModal = false;
        this.refresh();
        this.$alert("User group created successfully.", "", "success");
      }).catch((error) => {
        if (error.response.data.message) {
          this.$alert(error.response.data.message, "", "error");
        } else this.$alert("An error occurred creating the user group.", "", "error");

        this.isLoading = false
      });
    },
    cancelCreatingUserGroup() {
      this.showCreateModal = false;
    }
  },
  created() {
    this.refresh()
  },
  validations: {
    userGroupBeingEdited: {
      title: {
        required: withParams({ msg: "Please enter a title." }, required),
        maxLength: withParams({ msg: "The title must be no longer than 50 characters." }, maxLength(50))
      }
    },
    newUserGroup: {
      title: {
        required: withParams({ msg: "Please enter a title." }, required),
        maxLength: withParams({ msg: "The title must be no longer than 50 characters." }, maxLength(50))
      }
    }
  }
}
</script>

<style lang="sass" scoped>
</style>